import _ from 'lodash';
import type { Def } from './erod-schema';
import { Schema } from './erod-schema';
import type { JsonSchema } from './erod-to-json-schema';

/**
 * Converts a JSON schema (the common subset) to an Erod schema so
 * runtime-supplied tool definitions (e.g. ad-hoc client-implemented tools) can
 * flow through the Erod-based tool pipeline (validation + LLM rendering).
 *
 * Supported: object (properties/required/description), string (+ enum/const),
 * number, integer, boolean, array (items), null, nullable unions
 * (`type: [T, 'null']`, `oneOf`/`anyOf` with `{type: 'null'}`), descriptions
 * and defaults.
 *
 * Anything unsupported falls back to `Schema.super()` (accepts any scalar)
 * with the raw JSON schema appended to the description so the LLM still sees
 * the intended shape.
 */
export function jsonSchemaToErod(schema: JsonSchema): Def<unknown> {
  return convert(schema);
}

function isJsonSchemaObject(value: unknown): value is JsonSchema {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function superFallback(schema: JsonSchema, description?: string): Def<unknown> {
  const rawSchema = JSON.stringify(schema);
  const fallbackDescription =
    description !== undefined && description !== ''
      ? `${description}\n\nJSON schema: ${rawSchema}`
      : `JSON schema: ${rawSchema}`;
  return Schema.super().describe(fallbackDescription);
}

function applyMetadata(def: Def<unknown>, schema: JsonSchema): Def<unknown> {
  let result = def;
  // Default before describe: Erod's `.default()` drops a prior description,
  // while `.describe()` preserves a prior default.
  if ('default' in schema) {
    result = result.default(schema['default']);
  }
  const description = schema['description'];
  if (typeof description === 'string' && description !== '') {
    result = result.describe(description);
  }
  return result;
}

function convert(schema: JsonSchema): Def<unknown> {
  const nullableResolved = resolveNullableWrapper(schema);
  if (nullableResolved !== null) {
    return applyMetadata(convert(nullableResolved).nullable(), schema);
  }

  const enumDef = convertEnum(schema);
  if (enumDef !== null) {
    return applyMetadata(enumDef, schema);
  }

  const type = schema['type'];
  if (typeof type !== 'string') {
    return superFallback(schema, stringOrUndefined(schema['description']));
  }

  switch (type) {
    case 'string':
      return applyMetadata(Schema.string(), schema);
    case 'integer':
      return applyMetadata(Schema.int(), schema);
    case 'number':
      return applyMetadata(Schema.number(), schema);
    case 'boolean':
      return applyMetadata(Schema.boolean(), schema);
    case 'null':
      return applyMetadata(Schema.super().nullable(), schema);
    case 'array': {
      const items = schema['items'];
      const elementDef = isJsonSchemaObject(items)
        ? convert(items)
        : Schema.super();
      return applyMetadata(Schema.array(elementDef), schema);
    }
    case 'object':
      return applyMetadata(convertObject(schema), schema);
    default:
      return superFallback(schema, stringOrUndefined(schema['description']));
  }
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Detects nullable wrappers and returns the non-null inner schema:
 * - `type: ['string', 'null']`
 * - `oneOf`/`anyOf` of exactly one schema plus `{type: 'null'}`
 * Returns null when the schema is not a nullable wrapper.
 */
function resolveNullableWrapper(schema: JsonSchema): JsonSchema | null {
  const type = schema['type'];
  if (Array.isArray(type)) {
    const nonNull = type.filter((t) => t !== 'null');
    if (nonNull.length === 1 && type.length === 2) {
      return { ...schema, type: nonNull[0] };
    }
    return null;
  }

  for (const key of ['oneOf', 'anyOf'] as const) {
    const options = schema[key];
    if (!Array.isArray(options) || options.length !== 2) {
      continue;
    }
    const objects = options.filter(isJsonSchemaObject);
    if (objects.length !== 2) {
      continue;
    }
    const nullOption = objects.find((o) => o['type'] === 'null');
    const valueOption = objects.find((o) => o['type'] !== 'null');
    if (nullOption !== undefined && valueOption !== undefined) {
      return valueOption;
    }
  }

  return null;
}

/** String enums map to `Schema.enum`; string `const` maps to `Schema.literal`. */
function convertEnum(schema: JsonSchema): Def<unknown> | null {
  const enumValues = schema['enum'];
  if (Array.isArray(enumValues) && enumValues.length > 0) {
    if (enumValues.every((v): v is string => typeof v === 'string')) {
      return Schema.enum(enumValues);
    }
    return superFallback(schema, stringOrUndefined(schema['description']));
  }

  const constValue = schema['const'];
  if (typeof constValue === 'string') {
    return Schema.literal(constValue);
  }

  return null;
}

function convertObject(schema: JsonSchema): Def<unknown> {
  const properties = schema['properties'];

  if (!isJsonSchemaObject(properties) || Object.keys(properties).length === 0) {
    // Free-form object: `additionalProperties` map or untyped object.
    const additionalProperties = schema['additionalProperties'];
    const valueDef = isJsonSchemaObject(additionalProperties)
      ? convert(additionalProperties)
      : Schema.super();
    return Schema.record(valueDef);
  }

  const required = Array.isArray(schema['required'])
    ? new Set(schema['required'].filter((k) => typeof k === 'string'))
    : new Set<string>();

  const fields = _.mapValues(properties, (fieldSchema, key) => {
    const fieldDef = isJsonSchemaObject(fieldSchema)
      ? convert(fieldSchema)
      : Schema.super();
    // Erod has no "optional": non-required fields become nullable (lenient
    // zod parse converts missing values to null).
    if (required.has(key) || fieldDef.isNullable) {
      return fieldDef;
    }
    return fieldDef.nullable().default(null);
  });

  return Schema.object(fields) as Def<unknown>;
}
